import { useState } from "react";
import { authClient } from "../api.ts";
import { useBrand, usePublicConfig } from "../hooks.ts";
import { Logo } from "../components/Shell.tsx";
import { Card, ErrorText, Spinner } from "../components/ui.tsx";

const providerMeta = {
  google: {
    label: "Continue with Google",
    icon: (
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
    ),
  },
  microsoft: {
    label: "Continue with Microsoft",
    icon: (
      <svg width="18" height="18" viewBox="0 0 23 23"><path fill="#f35325" d="M1 1h10v10H1z"/><path fill="#81bc06" d="M12 1h10v10H12z"/><path fill="#05a6f0" d="M1 12h10v10H1z"/><path fill="#ffba08" d="M12 12h10v10H12z"/></svg>
    ),
  },
} as const;

export function LoginPage() {
  const cfg = usePublicConfig();
  const brand = useBrand();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(() => new URLSearchParams(location.search).get("error"));
  // Only same-site paths: never let ?next= send someone to another origin after sign-in.
  const next = (() => {
    const n = new URLSearchParams(location.search).get("next") ?? "/";
    return n.startsWith("/") && !n.startsWith("//") && !n.startsWith("/\\") ? n : "/";
  })();

  return (
    <div className="relative grid min-h-full place-items-center overflow-hidden px-4">
      <Card className="relative w-full max-w-sm overflow-hidden shadow-sm">
        <div className="grid place-items-center bg-[var(--chrome)] py-8"><Logo className="h-8" /></div>
        <div className="p-8">
        <h1 className="text-xl font-semibold tracking-tight">Sign in</h1>
        <p className="mt-1.5 text-sm text-[var(--muted)]">Use your work account to reach your AI assistant.</p>
        <div className="mt-6 space-y-2.5">
          {cfg.isPending && <div className="grid place-items-center py-4"><Spinner /></div>}
          {cfg.data?.providers.map((p) => (
            <button
              key={p}
              disabled={!!busy}
              onClick={async () => {
                setBusy(p);
                setError(null);
                const r = await authClient.signIn.social({ provider: p, callbackURL: next, errorCallbackURL: "/login" });
                if (r.error) {
                  setError(r.error.message ?? "Sign-in failed");
                  setBusy(null);
                }
              }}
              className="flex h-11 w-full items-center justify-center gap-3 rounded-lg border border-[var(--line)] bg-[var(--panel)] text-sm font-medium transition hover:bg-[var(--panel-2)] disabled:opacity-60"
            >
              {busy === p ? <Spinner /> : providerMeta[p].icon}
              {providerMeta[p].label}
            </button>
          ))}
          {cfg.data && cfg.data.providers.length === 0 && (
            <p className="text-sm text-[var(--muted)]">No sign-in method is configured yet. An administrator needs to set one up.</p>
          )}
          <ErrorText error={error ? friendly(String(error instanceof Error ? error.message : error), brand.name) : null} />
        </div>
        </div>
      </Card>
    </div>
  );
}

function friendly(code: string, name: string) {
  if (/not been invited|forbidden|unable_to_create_user/i.test(code)) return `Your account hasn't been invited to ${name} yet. Ask an admin to add you.`;
  if (/disabled/i.test(code)) return `Your ${name} account is disabled.`;
  return code.replaceAll("_", " ");
}
